import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Plus, Trash2, Vote, Play, Square, CheckCircle2, AlertTriangle, Award, Users, Download, FileText, ChevronRight, ArrowLeft, X, Copy, UserCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import MemberPicker from "../MemberPicker";

/**
 * Admin dashboard for elections. Two-level UI:
 *   - list of elections + "New election" button, plus an "Appointments"
 *     sub-tab tracking members who want to be appointed to Regional
 *     Governor / Director roles (the non-voted counterpart)
 *   - drill-down into a single election (candidates, results, runoff, cert)
 */
export default function ElectionsAdmin() {
    const [items, setItems] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [tiers, setTiers] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const [editingElection, setEditingElection] = useState(null);
    const [subTab, setSubTab] = useState("elections"); // 'elections' | 'appointments'

    async function load() {
        try {
            const { data } = await api.get("/admin/elections");
            setItems(data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not load elections");
        }
    }
    useEffect(() => {
        load();
        api.get("/chapters").then(({ data }) => setChapters(data)).catch(() => {});
        api.get("/tiers").then(({ data }) => setTiers(data)).catch(() => {});
    }, []);

    async function duplicate(id) {
        try {
            const { data } = await api.post(`/admin/elections/${id}/duplicate`);
            toast.success(`Duplicated as draft — edit dates & title, then save`);
            await load();
            // Open the freshly duplicated election in the edit dialog so
            // the admin can immediately rename it and set new dates.
            setEditingElection(data);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Duplicate failed");
        }
    }

    if (selectedId) {
        return <ElectionDetail electionId={selectedId} onBack={() => { setSelectedId(null); load(); }} chapters={chapters} tiers={tiers} />;
    }

    return (
        <div data-testid="elections-admin">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Vote className="h-5 w-5 text-primary" /> Elections</h2>
                    <p className="text-sm text-muted-foreground">Create elections, review write-ins, and certify results.</p>
                </div>
                {subTab === "elections" && (
                    <Button onClick={() => setShowNew(true)} className="rounded-full" data-testid="new-election-btn"><Plus className="h-4 w-4 mr-1" /> New election</Button>
                )}
            </div>
            <div className="inline-flex gap-1 rounded-full bg-muted p-1 mb-4" role="tablist" data-testid="elections-subtabs">
                <button
                    type="button"
                    onClick={() => setSubTab("elections")}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${subTab === "elections" ? "bg-white shadow text-primary" : "text-slate-600 hover:text-slate-900"}`}
                    data-testid="elections-subtab-elections"
                >
                    <Vote className="h-3.5 w-3.5 inline mr-1" /> Elections
                </button>
                <button
                    type="button"
                    onClick={() => setSubTab("appointments")}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${subTab === "appointments" ? "bg-white shadow text-primary" : "text-slate-600 hover:text-slate-900"}`}
                    data-testid="elections-subtab-appointments"
                >
                    <UserCheck className="h-3.5 w-3.5 inline mr-1" /> Appointments
                </button>
            </div>
            {subTab === "elections" ? (
                <div className="space-y-2">
                    {items.length === 0 && <div className="p-6 text-muted-foreground text-center rounded-2xl border border-dashed">No elections yet.</div>}
                    {items.map((e) => (
                        <ElectionRow
                            key={e.id}
                            e={e}
                            onOpen={() => setSelectedId(e.id)}
                            onDuplicate={() => duplicate(e.id)}
                            onEdit={() => setEditingElection(e)}
                        />
                    ))}
                </div>
            ) : (
                <AppointmentsAdmin />
            )}
            {showNew && <NewElectionDialog onClose={() => setShowNew(false)} onCreated={(id) => { setShowNew(false); load(); setSelectedId(id); }} chapters={chapters} tiers={tiers} />}
            {editingElection && (
                <EditElectionDialog
                    election={editingElection}
                    chapters={chapters}
                    tiers={tiers}
                    onClose={() => setEditingElection(null)}
                    onSaved={(id) => { setEditingElection(null); load(); setSelectedId(id); }}
                />
            )}
        </div>
    );
}

function statusColor(s) {
    return {
        draft: "bg-slate-100 text-slate-700",
        scheduled: "bg-blue-100 text-blue-700",
        open: "bg-emerald-100 text-emerald-800",
        closed: "bg-amber-100 text-amber-800",
        under_review: "bg-amber-100 text-amber-800",
        runoff_required: "bg-orange-100 text-orange-800",
        runoff_open: "bg-emerald-100 text-emerald-800",
        runoff_closed: "bg-amber-100 text-amber-800",
        certified: "bg-primary/15 text-primary",
    }[s] || "bg-slate-100";
}

function ElectionRow({ e, onOpen, onDuplicate, onEdit }) {
    const canEdit = e.status === "draft" || e.status === "scheduled";
    return (
        <div
            className="w-full rounded-2xl border border-slate-200 bg-white hover:border-primary/40 hover:shadow p-4 flex items-center gap-3"
            data-testid={`admin-election-row-${e.id}`}
        >
            <button
                type="button"
                onClick={onOpen}
                className="flex-1 min-w-0 text-left cursor-pointer"
                data-testid={`admin-election-open-${e.id}`}
            >
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-semibold truncate">{e.name}</span>
                    <Badge className={`${statusColor(e.status)} rounded-full text-[10px] uppercase tracking-wider font-semibold`}>{e.status.replace(/_/g, " ")}</Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                    <span>{e.offices?.length || 0} office{(e.offices?.length || 0) === 1 ? "" : "s"}</span>
                    <span>· {e.ballot_count || 0} ballot{(e.ballot_count || 0) === 1 ? "" : "s"} of {e.eligible_count || 0}</span>
                    <span>· {e.starts_at ? new Date(e.starts_at).toLocaleDateString() : "no start"} → {e.closes_at ? new Date(e.closes_at).toLocaleDateString() : "no close"}</span>
                </div>
            </button>
            {canEdit && onEdit && (
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-full shrink-0"
                    onClick={(ev) => { ev.stopPropagation(); onEdit(); }}
                    data-testid={`admin-election-edit-${e.id}`}
                    title="Edit basic details"
                >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
            )}
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full shrink-0"
                onClick={(ev) => { ev.stopPropagation(); onDuplicate && onDuplicate(); }}
                data-testid={`admin-election-duplicate-${e.id}`}
                title="Duplicate this election as a new draft"
            >
                <Copy className="h-3.5 w-3.5 mr-1" /> Duplicate
            </Button>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        </div>
    );
}

function NewElectionDialog({ onClose, onCreated, chapters, tiers }) {
    const [form, setForm] = useState({
        name: "", description: "", election_type: "general",
        starts_at: "", closes_at: "",
        chapter_ids: [], tier_ids: [], rules: "",
        offices: [{ title: "", description: "", positions: 1, allow_writeins: true, majority_required: true, position_type: "other" }],
    });
    const [busy, setBusy] = useState(false);

    function setOffice(i, patch) {
        setForm((f) => ({ ...f, offices: f.offices.map((o, idx) => idx === i ? { ...o, ...patch } : o) }));
    }
    function toISO(v) { return v ? new Date(v).toISOString() : ""; }

    async function save() {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (!form.starts_at || !form.closes_at) { toast.error("Set start and close dates"); return; }
        if (form.offices.length === 0 || form.offices.some((o) => !o.title.trim())) { toast.error("Every office needs a title"); return; }
        setBusy(true);
        try {
            const payload = {
                ...form,
                starts_at: toISO(form.starts_at),
                closes_at: toISO(form.closes_at),
            };
            const { data } = await api.post("/admin/elections", payload);
            toast.success("Election created (draft)");
            onCreated(data.id);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not create election");
        } finally { setBusy(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>New Election</DialogTitle>
                    <DialogDescription>Create a draft election with offices, audience scoping, and voting window. You can refine details after saving.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div><Label>Election name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-election-name" /></div>
                    <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>Starts</Label><Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="new-election-starts" /></div>
                        <div><Label>Closes</Label><Input type="datetime-local" value={form.closes_at} onChange={(e) => setForm({ ...form, closes_at: e.target.value })} data-testid="new-election-closes" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <MultiSelect label="Restrict to chapters (blank = all)" options={chapters.map((c) => ({ id: c.id, label: c.name }))} value={form.chapter_ids} onChange={(v) => setForm({ ...form, chapter_ids: v })} testid="new-election-chapters" />
                        <MultiSelect label="Restrict to tiers (blank = all)" options={tiers.map((t) => ({ id: t.id, label: t.name }))} value={form.tier_ids} onChange={(v) => setForm({ ...form, tier_ids: v })} testid="new-election-tiers" />
                    </div>
                    <div>
                        <Label>Rules / notes to voters (optional)</Label>
                        <Textarea rows={2} value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} />
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label>Offices</Label>
                            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setForm((f) => ({ ...f, offices: [...f.offices, { title: "", description: "", positions: 1, allow_writeins: true, majority_required: true, position_type: "other" }] }))} data-testid="add-office-btn"><Plus className="h-3 w-3 mr-1" />Office</Button>
                        </div>
                        <div className="space-y-3">
                            {form.offices.map((o, i) => (
                                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Input placeholder="Office title (e.g. President)" value={o.title} onChange={(e) => setOffice(i, { title: e.target.value })} data-testid={`office-title-${i}`} />
                                        {form.offices.length > 1 && (
                                            <Button size="icon" variant="ghost" onClick={() => setForm((f) => ({ ...f, offices: f.offices.filter((_, idx) => idx !== i) }))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                        )}
                                    </div>
                                    <Input placeholder="Description (optional)" value={o.description} onChange={(e) => setOffice(i, { description: e.target.value })} />
                                    <div>
                                        <Label className="text-xs">Position type (drives auto-eligibility)</Label>
                                        <Select value={o.position_type || "other"} onValueChange={(v) => setOffice(i, { position_type: v })}>
                                            <SelectTrigger className="h-9" data-testid={`office-position-type-${i}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="president_vp">President / Vice President</SelectItem>
                                                <SelectItem value="lt_governor">Lieutenant Governor</SelectItem>
                                                <SelectItem value="other">Other (no auto-check)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 text-xs">
                                        <label className="flex items-center gap-2">
                                            <Checkbox checked={o.allow_writeins} onCheckedChange={(v) => setOffice(i, { allow_writeins: !!v })} />
                                            Allow write-ins
                                        </label>
                                        <label className="flex items-center gap-2">
                                            <Checkbox checked={o.majority_required} onCheckedChange={(v) => setOffice(i, { majority_required: !!v })} />
                                            Require majority (AOP default)
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={save} disabled={busy} data-testid="create-election-btn">{busy ? "Creating…" : "Create draft"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ============================================================
   Edit dialog — reused for both editing existing draft/scheduled
   elections AND for the "duplicate → edit immediately" flow so
   admins can adjust dates/title right after cloning.
   ============================================================ */

function EditElectionDialog({ election, chapters, tiers, onClose, onSaved }) {
    const toLocal = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const [form, setForm] = useState({
        name: election.name || "",
        description: election.description || "",
        election_type: election.election_type || "general",
        starts_at: toLocal(election.starts_at),
        closes_at: toLocal(election.closes_at),
        chapter_ids: election.chapter_ids || [],
        tier_ids: election.tier_ids || [],
        rules: election.rules || "",
    });
    const [busy, setBusy] = useState(false);

    async function save() {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (!form.starts_at || !form.closes_at) { toast.error("Set start and close dates"); return; }
        setBusy(true);
        try {
            await api.put(`/admin/elections/${election.id}`, {
                name: form.name,
                description: form.description,
                election_type: form.election_type,
                starts_at: new Date(form.starts_at).toISOString(),
                closes_at: new Date(form.closes_at).toISOString(),
                chapter_ids: form.chapter_ids,
                tier_ids: form.tier_ids,
                rules: form.rules,
            });
            toast.success("Saved");
            onSaved && onSaved(election.id);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not save");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-election-dialog">
                <DialogHeader>
                    <DialogTitle>Edit Election</DialogTitle>
                    <DialogDescription>Update the name, dates, audience scoping and voter notes. Office titles are locked once created — open the election to change position types or add candidates.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label>Election name</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            data-testid="edit-election-name"
                        />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Starts</Label>
                            <Input
                                type="datetime-local"
                                value={form.starts_at}
                                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                                data-testid="edit-election-starts"
                            />
                        </div>
                        <div>
                            <Label>Closes</Label>
                            <Input
                                type="datetime-local"
                                value={form.closes_at}
                                onChange={(e) => setForm({ ...form, closes_at: e.target.value })}
                                data-testid="edit-election-closes"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <MultiSelect
                            label="Restrict to chapters (blank = all)"
                            options={chapters.map((c) => ({ id: c.id, label: c.name }))}
                            value={form.chapter_ids}
                            onChange={(v) => setForm({ ...form, chapter_ids: v })}
                            testid="edit-election-chapters"
                        />
                        <MultiSelect
                            label="Restrict to tiers (blank = all)"
                            options={tiers.map((t) => ({ id: t.id, label: t.name }))}
                            value={form.tier_ids}
                            onChange={(v) => setForm({ ...form, tier_ids: v })}
                            testid="edit-election-tiers"
                        />
                    </div>
                    <div>
                        <Label>Rules / notes to voters (optional)</Label>
                        <Textarea rows={2} value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} />
                    </div>
                    {(election.offices && election.offices.length > 0) && (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                                Offices ({election.offices.length})
                            </div>
                            <ul className="text-sm space-y-1">
                                {election.offices.map((o) => (
                                    <li key={o.id} className="flex items-center justify-between gap-2">
                                        <span className="truncate">{o.title || "(untitled)"}</span>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                            {(o.position_type || "other").replace("_", "/")}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-[11px] text-muted-foreground mt-2">
                                Office titles are locked once created. Open the election to change position types, add candidates, or remove offices.
                            </p>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={save} disabled={busy} data-testid="edit-election-save-btn">
                        {busy ? "Saving…" : "Save & open"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MultiSelect({ label, options, value, onChange, testid }) {
    return (
        <div>
            <Label>{label}</Label>
            <div className="mt-1.5 rounded-xl border border-slate-200 bg-white p-2 max-h-32 overflow-y-auto text-sm" data-testid={testid}>
                {options.length === 0 && <div className="text-xs text-muted-foreground p-1">None available.</div>}
                {options.map((opt) => (
                    <label key={opt.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 rounded px-1">
                        <Checkbox
                            checked={value.includes(opt.id)}
                            onCheckedChange={(v) => onChange(v ? [...value, opt.id] : value.filter((x) => x !== opt.id))}
                        />
                        <span className="truncate">{opt.label}</span>
                    </label>
                ))}
            </div>
        </div>
    );
}

// ---------- Detail view ----------

function ElectionDetail({ electionId, onBack, chapters, tiers }) {
    const [e, setE] = useState(null);
    const [tab, setTab] = useState("overview");    // overview | candidates | writeins | results | runoff | cert
    const [reload, setReload] = useState(0);

    useEffect(() => {
        api.get("/admin/elections").then(({ data }) => {
            const found = data.find((x) => x.id === electionId);
            setE(found);
        }).catch(() => {});
    }, [electionId, reload]);

    if (!e) return <div className="text-muted-foreground">Loading…</div>;

    async function changeStatus(next) {
        try {
            await api.post(`/admin/elections/${electionId}/status`, { status: next });
            toast.success(`Status: ${next}`);
            setReload((r) => r + 1);
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Could not change status");
        }
    }

    async function del() {
        const label = e.status === "draft" ? "Delete this draft election?" :
            `Delete this ${e.status.replace(/_/g, " ")} election? All ballots, receipts, and any runoffs will be removed. This cannot be undone.`;
        if (!confirm(label)) return;
        try {
            await api.delete(`/admin/elections/${electionId}`);
            toast.success("Deleted");
            onBack();
        } catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Could not delete"); }
    }

    return (
        <div data-testid="admin-election-detail">
            <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4">
                <ArrowLeft className="h-4 w-4" /> Back to elections
            </button>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="font-heading text-2xl font-bold">{e.name}</h2>
                        <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                            <span>{new Date(e.starts_at).toLocaleString()} → {new Date(e.closes_at).toLocaleString()}</span>
                            <Badge className={`${statusColor(e.status)} rounded-full text-[10px] uppercase tracking-wider font-semibold`}>{e.status.replace(/_/g, " ")}</Badge>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {e.status === "draft" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => changeStatus("scheduled")} data-testid="schedule-btn">Schedule</Button>}
                        {(e.status === "draft" || e.status === "scheduled") && <Button size="sm" className="rounded-full" onClick={() => changeStatus("open")} data-testid="open-btn"><Play className="h-3 w-3 mr-1" /> Open voting</Button>}
                        {e.status === "open" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => changeStatus("closed")} data-testid="close-btn"><Square className="h-3 w-3 mr-1" /> Close voting</Button>}
                        {["closed", "runoff_closed"].includes(e.status) && <Button size="sm" variant="outline" className="rounded-full" onClick={() => changeStatus("under_review")} data-testid="review-btn">Move to review</Button>}
                        {["under_review", "runoff_closed", "closed"].includes(e.status) && <Button size="sm" className="rounded-full" onClick={() => changeStatus("certified")} data-testid="certify-btn"><CheckCircle2 className="h-3 w-3 mr-1" /> Certify</Button>}
                        {e.status !== "certified" && <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={del} data-testid="delete-election-btn"><Trash2 className="h-3 w-3 mr-1" />Delete</Button>}
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/elections/${electionId}/export.csv`, "_blank")}><Download className="h-3 w-3 mr-1" /> CSV</Button>
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/elections/${electionId}/export.pdf`, "_blank")}><FileText className="h-3 w-3 mr-1" /> PDF</Button>
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-1 mb-4 border-b border-slate-200">
                {[
                    ["overview", "Overview"], ["candidates", "Candidates"], ["writeins", "Write-in Review"], ["results", "Results"], ["runoff", "Runoff"], ["audit", "Audit"],
                ].map(([k, label]) => (
                    <button key={k}
                        onClick={() => setTab(k)}
                        className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                        data-testid={`el-tab-${k}`}
                    >{label}</button>
                ))}
            </div>

            {tab === "overview" && <Overview e={e} />}
            {tab === "candidates" && <CandidatesTab electionId={electionId} offices={e.offices} onChange={() => setReload((r) => r + 1)} />}
            {tab === "writeins" && <WriteInsTab electionId={electionId} onChange={() => setReload((r) => r + 1)} />}
            {tab === "results" && <ResultsTab electionId={electionId} />}
            {tab === "runoff" && <RunoffTab electionId={electionId} onChange={() => setReload((r) => r + 1)} />}
            {tab === "audit" && <AuditTab electionId={electionId} />}
        </div>
    );
}

function Overview({ e }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Eligible" value={e.eligible_count || 0} icon={<Users className="h-4 w-4" />} />
            <Stat label="Ballots cast" value={e.ballot_count || 0} icon={<Vote className="h-4 w-4" />} />
            <Stat label="Turnout" value={`${e.eligible_count ? Math.round(100 * e.ballot_count / e.eligible_count) : 0}%`} icon={<Award className="h-4 w-4" />} />
            <div className="sm:col-span-3 rounded-2xl border border-slate-200 p-4 bg-white">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Offices</div>
                <div className="space-y-2">
                    {e.offices?.map((o) => (
                        <div key={o.id} className="rounded-xl border border-slate-200 p-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="font-semibold">{o.title}</div>
                                    {o.description && <div className="text-xs text-muted-foreground mt-0.5">{o.description}</div>}
                                </div>
                                <div className="flex items-center gap-2 text-[10px] uppercase font-semibold tracking-wider">
                                    {o.allow_writeins && <Badge variant="outline" className="rounded-full">Write-ins</Badge>}
                                    <Badge variant="outline" className="rounded-full">{o.majority_required ? "Majority" : "Plurality"}</Badge>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
function Stat({ label, value, icon }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{icon} {label}</div>
            <div className="text-3xl font-heading font-bold mt-1">{value}</div>
        </div>
    );
}

function CandidatesTab({ electionId, offices, onChange }) {
    const [candidates, setCandidates] = useState([]);
    const [members, setMembers] = useState([]);
    const [form, setForm] = useState({
        office_id: offices?.[0]?.id || "",
        name: "",
        statement: "",
        member_id: "",
        letter_of_recommendation: false,
        letter_notes: "",
        leadership_held: false,
        leadership_notes: "",
    });
    const [previewEligibility, setPreviewEligibility] = useState(null);

    async function load() {
        const [c, m] = await Promise.all([
            api.get(`/admin/elections/${electionId}/candidates`).then((r) => r.data).catch(() => []),
            api.get("/admin/elections/eligible-members").then((r) => r.data).catch(() => []),
        ]);
        setCandidates(c);
        setMembers(m);
    }
    useEffect(() => { load(); }, [electionId]);

    // When the admin picks a member + an auto-checkable office, live-preview the eligibility.
    useEffect(() => {
        if (!form.member_id || !form.office_id) { setPreviewEligibility(null); return; }
        const office = offices.find((o) => o.id === form.office_id);
        if (!office || office.position_type === "other" || !office.position_type) {
            setPreviewEligibility(null);
            return;
        }
        api.get(`/admin/elections/${electionId}/eligibility-check`, {
            params: { member_id: form.member_id, office_id: form.office_id },
        }).then(({ data }) => setPreviewEligibility(data)).catch(() => setPreviewEligibility(null));
    }, [form.member_id, form.office_id, electionId, offices]);

    async function add() {
        if (!form.name.trim() || !form.office_id) { toast.error("Pick an office and a member (or type a name)"); return; }
        try {
            await api.post(`/admin/elections/${electionId}/candidates`, form);
            toast.success("Candidate added");
            setForm({ office_id: form.office_id, name: "", statement: "", member_id: "", letter_of_recommendation: false, letter_notes: "", leadership_held: false, leadership_notes: "" });
            setPreviewEligibility(null);
            load();
            onChange();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not add"); }
    }

    async function setStatus(cid, status) {
        try {
            await api.post(`/admin/elections/${electionId}/candidates/${cid}/status`, { status });
            toast.success(`Marked ${status}`);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not update"); }
    }
    async function setFlags(cid, patch) {
        try {
            await api.post(`/admin/elections/${electionId}/candidates/${cid}/flags`, patch);
            load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not update"); }
    }
    async function del(cid) {
        if (!confirm("Remove this candidate?")) return;
        try { await api.delete(`/admin/elections/${electionId}/candidates/${cid}`); load(); toast.success("Removed"); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not remove"); }
    }

    const currentOffice = offices.find((o) => o.id === form.office_id);

    return (
        <div className="space-y-4" data-testid="candidates-tab">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Add candidate</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label>Office</Label>
                        <Select value={form.office_id} onValueChange={(v) => setForm({ ...form, office_id: v })}>
                            <SelectTrigger data-testid="cand-office-select"><SelectValue placeholder="Pick an office…" /></SelectTrigger>
                            <SelectContent>
                                {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.title} <span className="text-xs text-muted-foreground ml-1">· {(o.position_type || "other").replace("_", "/")}</span></SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>AOP Member</Label>
                        <MemberPicker
                            options={members}
                            value={form.member_id}
                            onChange={(m) => setForm({ ...form, member_id: m?.id || "", name: m?.name || form.name })}
                            placeholder="Search active members…"
                            testid="cand-member-picker"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">Only shows active members. Leave blank for an external candidate.</p>
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Name on ballot</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cand-name-input" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Candidate statement</Label>
                        <Textarea rows={3} value={form.statement} onChange={(e) => setForm({ ...form, statement: e.target.value })} data-testid="cand-statement-input" />
                    </div>
                    <div className="sm:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3 border-t pt-3">
                        <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                            <label className="flex items-center gap-2 font-semibold cursor-pointer" data-testid="letter-checkbox">
                                <Checkbox checked={form.letter_of_recommendation} onCheckedChange={(v) => setForm({ ...form, letter_of_recommendation: !!v })} />
                                Letter of recommendation received
                            </label>
                            <Textarea rows={2} placeholder="Notes (optional)" value={form.letter_notes} onChange={(e) => setForm({ ...form, letter_notes: e.target.value })} />
                        </div>
                        <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                            <label className="flex items-center gap-2 font-semibold cursor-pointer" data-testid="leadership-checkbox">
                                <Checkbox checked={form.leadership_held} onCheckedChange={(v) => setForm({ ...form, leadership_held: !!v })} />
                                Held a leadership position
                            </label>
                            <Textarea rows={2} placeholder="Notes (optional)" value={form.leadership_notes} onChange={(e) => setForm({ ...form, leadership_notes: e.target.value })} />
                        </div>
                    </div>
                </div>
                {previewEligibility && (
                    <div className="mt-4">
                        <EligibilityPanel report={previewEligibility} />
                    </div>
                )}
                {form.member_id && currentOffice && (currentOffice.position_type === "other" || !currentOffice.position_type) && (
                    <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="office-untagged-warning">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                            <div className="text-sm">
                                This office isn&apos;t tagged as President/VP or Lieutenant Governor, so no auto-eligibility check will run. Set the position type on the office below (in the {currentOffice.title} section) to unlock the automated criteria check.
                            </div>
                        </div>
                    </div>
                )}
                <div className="mt-3 flex justify-end">
                    <Button onClick={add} className="rounded-full" data-testid="add-candidate-btn"><Plus className="h-4 w-4 mr-1" />Add candidate</Button>
                </div>
            </div>

            <div className="space-y-3">
                {offices.map((o) => {
                    const list = candidates.filter((c) => c.office_id === o.id);
                    return (
                        <div key={o.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                                <div className="font-semibold">{o.title}</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Position type</span>
                                    <Select
                                        value={o.position_type || "other"}
                                        onValueChange={async (v) => {
                                            try {
                                                await api.put(`/admin/elections/${electionId}/offices/${o.id}`, { position_type: v });
                                                toast.success("Position type saved");
                                                onChange();
                                                load();
                                            } catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Could not update"); }
                                        }}
                                    >
                                        <SelectTrigger className="h-8 w-56" data-testid={`office-position-type-live-${o.id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="president_vp">President / Vice President</SelectItem>
                                            <SelectItem value="lt_governor">Lieutenant Governor</SelectItem>
                                            <SelectItem value="other">Other (no auto-check)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            {list.length === 0 && <div className="text-sm text-muted-foreground">No candidates yet.</div>}
                            <div className="space-y-3">
                                {list.map((c) => (
                                    <CandidateRow
                                        key={c.id}
                                        candidate={c}
                                        electionId={electionId}
                                        officePositionType={o.position_type || "other"}
                                        onStatus={(s) => setStatus(c.id, s)}
                                        onFlags={(patch) => setFlags(c.id, patch)}
                                        onDelete={() => del(c.id)}
                                        onRecheck={load}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function CandidateRow({ candidate: c, officePositionType, onStatus, onFlags, onDelete, onRecheck, electionId }) {
    const [showEligibility, setShowEligibility] = useState(false);
    const [busyRecheck, setBusyRecheck] = useState(false);
    const canAutoCheck = c.member_id && (officePositionType === "president_vp" || officePositionType === "lt_governor");
    async function recheck() {
        if (!canAutoCheck) return;
        setBusyRecheck(true);
        try {
            await api.post(`/admin/elections/${electionId}/candidates/${c.id}/recheck`);
            toast.success("Eligibility re-checked");
            onRecheck?.();
            setShowEligibility(true);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not re-check"); }
        finally { setBusyRecheck(false); }
    }
    return (
        <div className="p-3 rounded-xl border border-slate-100 bg-slate-50/50" data-testid={`cand-row-${c.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-medium truncate flex items-center gap-2 flex-wrap">
                        {c.name}
                        {c.is_writein && <Badge variant="outline" className="rounded-full text-[10px]">write-in</Badge>}
                        {c.eligibility_report && (
                            <Badge className={`rounded-full text-[10px] ${c.eligibility_report.auto_pass ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                                {c.eligibility_report.auto_pass ? "Auto-eligible" : "Review required"}
                            </Badge>
                        )}
                        {!c.eligibility_report && c.member_id && (
                            canAutoCheck
                                ? <Badge className="rounded-full text-[10px] bg-slate-100 text-slate-700">Not checked yet</Badge>
                                : <Badge className="rounded-full text-[10px] bg-slate-100 text-slate-600">Office is Other — manual review</Badge>
                        )}
                    </div>
                    {c.statement && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{c.statement}</div>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {canAutoCheck && (
                        <Button variant="outline" size="sm" className="rounded-full" onClick={recheck} disabled={busyRecheck} data-testid={`cand-recheck-${c.id}`}>
                            {busyRecheck ? "Checking…" : (c.eligibility_report ? "Re-check" : "Check eligibility")}
                        </Button>
                    )}
                    <Select value={c.status || "qualified"} onValueChange={onStatus}>
                        <SelectTrigger className="h-8 w-40" data-testid={`cand-status-${c.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="qualified">Allow on ballot</SelectItem>
                            <SelectItem value="pending">Pending Verification</SelectItem>
                            <SelectItem value="ineligible">Deny — Ineligible</SelectItem>
                            <SelectItem value="disqualified">Disqualified</SelectItem>
                            <SelectItem value="declined">Declined</SelectItem>
                        </SelectContent>
                    </Select>
                    {c.eligibility_report && (
                        <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setShowEligibility((s) => !s)} data-testid={`cand-eligibility-toggle-${c.id}`}>
                            {showEligibility ? "Hide" : "Eligibility"}
                        </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={onDelete}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
            </div>
            {showEligibility && c.eligibility_report && (
                <div className="mt-3">
                    <EligibilityPanel report={c.eligibility_report} />
                </div>
            )}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!c.letter_of_recommendation} onCheckedChange={(v) => onFlags({ letter_of_recommendation: !!v })} />
                    Letter of recommendation received
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!c.leadership_held} onCheckedChange={(v) => onFlags({ leadership_held: !!v })} />
                    Held a leadership position
                </label>
            </div>
        </div>
    );
}

function EligibilityPanel({ report }) {
    if (!report || !report.criteria || report.criteria.length === 0) return null;
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="eligibility-panel">
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Auto-eligibility check
                </div>
                <Badge className={`rounded-full text-[10px] ${report.auto_pass ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                    {report.auto_pass ? "Meets automatable criteria" : "One or more criteria unmet"}
                </Badge>
            </div>
            <div className="space-y-1.5 text-sm">
                {report.criteria.map((c) => (
                    <div key={c.key} className="grid grid-cols-[auto_1fr_auto] gap-3 items-start">
                        {c.pass === true && <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />}
                        {c.pass === false && <X className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />}
                        {c.pass === null && <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />}
                        <div className="min-w-0">
                            <div className="font-medium truncate">{c.label}</div>
                            <div className="text-xs text-muted-foreground">{c.value} · required {c.required}</div>
                            {c.note && <div className="text-[11px] text-amber-700 mt-0.5">{c.note}</div>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function WriteInsTab({ electionId, onChange }) {
    const [buckets, setBuckets] = useState([]);
    async function load() {
        try { const { data } = await api.get(`/admin/elections/${electionId}/writein-review`); setBuckets(data); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not load"); }
    }
    useEffect(() => { load(); }, [electionId]);

    async function promote(office_id, bucket, status) {
        try {
            await api.post(`/admin/elections/${electionId}/writein-review`, {
                office_id, writein_norm: bucket.writein_norm, display_name: bucket.display_name, status,
            });
            toast.success(`Promoted as ${status}`);
            load();
            onChange();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not promote"); }
    }

    return (
        <div className="space-y-3" data-testid="writeins-tab">
            <div className="text-sm text-muted-foreground">
                Write-in names typed by voters are grouped after normalization. Promote a bucket to a candidate row (Qualified / Ineligible / Pending) so results can be computed.
            </div>
            {buckets.length === 0 && <div className="p-6 text-muted-foreground text-center rounded-2xl border border-dashed">No unmatched write-ins.</div>}
            {buckets.map((b) => (
                <div key={b.office_id} className="rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="font-semibold mb-2">{b.office_title}</div>
                    {b.buckets.length === 0 && <div className="text-sm text-muted-foreground">No write-ins for this office.</div>}
                    <div className="space-y-2">
                        {b.buckets.map((bk) => (
                            <div key={bk.writein_norm} className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-xl bg-slate-50">
                                <div>
                                    <div className="font-medium">{bk.display_name} <span className="text-xs text-muted-foreground">({bk.votes} vote{bk.votes === 1 ? "" : "s"})</span></div>
                                    <div className="text-xs text-muted-foreground truncate">Variants: {bk.raw_variants?.join(", ") || bk.display_name}</div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => promote(b.office_id, bk, "qualified")}>Qualify</Button>
                                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => promote(b.office_id, bk, "pending")}>Pending</Button>
                                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => promote(b.office_id, bk, "ineligible")}>Ineligible</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ResultsTab({ electionId }) {
    const [res, setRes] = useState(null);
    useEffect(() => { api.get(`/admin/elections/${electionId}/results`).then(({ data }) => setRes(data)).catch(() => {}); }, [electionId]);
    if (!res) return <div className="text-muted-foreground">Loading…</div>;
    return (
        <div className="space-y-4" data-testid="results-tab">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Stat label="Eligible" value={res.eligible_count} icon={<Users className="h-4 w-4" />} />
                <Stat label="Ballots cast" value={res.ballots_cast} icon={<Vote className="h-4 w-4" />} />
                <Stat label="Turnout" value={`${res.turnout_pct.toFixed(1)}%`} icon={<Award className="h-4 w-4" />} />
            </div>
            {res.offices.map((o) => <OfficeResult key={o.office_id} o={o} />)}
        </div>
    );
}
function OfficeResult({ o }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`office-result-${o.office_id}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-heading font-bold">{o.office_title}</div>
                    <div className="text-xs text-muted-foreground">{o.tally.total_ballots} ballots · {o.tally.valid_votes} valid · {o.tally.invalid_votes} invalid</div>
                </div>
                <div className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${o.outcome.kind === "winner" ? "bg-emerald-100 text-emerald-700" : o.outcome.kind === "runoff" ? "bg-orange-100 text-orange-700" : o.outcome.kind === "tie" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>
                    {o.outcome.kind.replace("_", " ")}
                </div>
            </div>
            {o.outcome.reason && <p className="text-xs text-muted-foreground mt-2 italic">{o.outcome.reason}</p>}
            <div className="mt-3 space-y-2">
                {o.tally.candidates.map((c) => (
                    <div key={c.id} className={`p-3 rounded-xl border ${c.id === o.outcome.winner_id ? "border-emerald-500 bg-emerald-50" : "border-slate-100 bg-slate-50/50"}`}>
                        <div className="flex items-center justify-between text-sm">
                            <div className="font-semibold">{c.name} {c.is_writein && <span className="text-[10px] uppercase text-muted-foreground">(write-in)</span>} {c.status !== "qualified" && <span className="text-[10px] uppercase text-amber-700">({c.status})</span>}</div>
                            <div className="tabular-nums font-mono">{c.votes} · {c.pct_of_valid.toFixed(1)}%</div>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                            <div className={`h-full ${c.id === o.outcome.winner_id ? "bg-emerald-500" : "bg-primary/60"}`} style={{ width: `${Math.min(100, c.pct_of_valid)}%` }} />
                        </div>
                    </div>
                ))}
            </div>
            {(o.unmatched_writeins || []).length > 0 && (
                <div className="mt-4 rounded-xl border border-dashed border-orange-300 bg-orange-50 p-3" data-testid={`unmatched-writeins-${o.office_id}`}>
                    <div className="text-xs uppercase tracking-wider text-orange-800 font-semibold mb-2">Unmatched write-in candidates ({o.unmatched_writeins.length})</div>
                    <p className="text-[11px] text-muted-foreground mb-2">
                        These names came from voters&apos; write-in fields but haven&apos;t been promoted to a candidate row yet. Their votes currently count as INVALID for majority math. Go to the Write-in Review tab to qualify or deny each.
                    </p>
                    <div className="space-y-1">
                        {o.unmatched_writeins.map((b) => (
                            <div key={b.writein_norm} className="flex items-center justify-between gap-3 text-sm">
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{b.display_name}</div>
                                    {b.raw_variants?.length > 1 && <div className="text-[11px] text-muted-foreground truncate">Variants: {b.raw_variants.join(", ")}</div>}
                                </div>
                                <div className="tabular-nums font-mono text-orange-700">{b.votes} vote{b.votes === 1 ? "" : "s"}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function RunoffTab({ electionId, onChange }) {
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get(`/admin/elections/${electionId}/results`).then(({ data }) => setRes(data)).catch(() => {}); }, [electionId]);
    if (!res) return <div className="text-muted-foreground">Loading…</div>;
    const runoffOffices = res.offices.filter((o) => o.outcome.kind === "runoff");
    async function createRunoff(o) {
        setBusy(true);
        try {
            const now = new Date();
            const start = new Date(now.getTime() + 60 * 60 * 1000).toISOString();
            const close = new Date(now.getTime() + 8 * 24 * 60 * 60 * 1000).toISOString();
            const { data } = await api.post(`/admin/elections/${electionId}/runoff`, {
                office_id: o.office_id, starts_at: start, closes_at: close, voting_instructions: "",
            });
            toast.success(`Runoff created: ${data.name}`);
            onChange();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not create runoff"); }
        finally { setBusy(false); }
    }
    return (
        <div className="space-y-3" data-testid="runoff-tab">
            {runoffOffices.length === 0 && (
                <div className="p-6 text-muted-foreground text-center rounded-2xl border border-dashed">
                    No runoff required based on current tallies.
                </div>
            )}
            {runoffOffices.map((o) => (
                <div key={o.office_id} className="rounded-2xl border border-orange-500/40 bg-orange-50 p-4 flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="font-heading font-bold">{o.office_title}</div>
                        <p className="text-sm text-muted-foreground italic">{o.outcome.reason}</p>
                    </div>
                    <Button className="rounded-full" onClick={() => createRunoff(o)} disabled={busy} data-testid={`create-runoff-${o.office_id}`}>
                        Create Runoff
                    </Button>
                </div>
            ))}
            {res.offices.some((o) => o.outcome.kind === "tie") && (
                <div className="rounded-2xl border-2 border-destructive/40 bg-destructive/5 p-4">
                    <div className="flex items-center gap-2 font-heading font-bold text-destructive"><AlertTriangle className="h-4 w-4" /> Tie flagged</div>
                    <p className="text-sm text-muted-foreground mt-1">
                        One or more offices are tied at a position that affects the outcome. Per AOP procedure, the authorized Election Committee must resolve the tie manually before certification.
                    </p>
                </div>
            )}
        </div>
    );
}

function AuditTab({ electionId }) {
    const [entries, setEntries] = useState([]);
    useEffect(() => { api.get(`/admin/elections/${electionId}/audit`).then(({ data }) => setEntries(data)).catch(() => {}); }, [electionId]);
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="audit-tab">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Recent actions</div>
            <div className="space-y-1 text-sm max-h-96 overflow-y-auto">
                {entries.length === 0 && <div className="text-muted-foreground">No audit entries yet.</div>}
                {entries.map((a) => (
                    <div key={a.id} className="grid grid-cols-[auto_1fr_auto] gap-3 items-baseline py-1 border-b border-slate-100 last:border-0">
                        <span className="text-xs text-muted-foreground tabular-nums">{new Date(a.at).toLocaleString()}</span>
                        <span><b>{a.action}</b> — {a.actor_name || a.actor_id}</span>
                        <span className="text-xs text-muted-foreground truncate max-w-xs">{a.details ? JSON.stringify(a.details) : ""}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ============================================================
   Appointments — Regional Governor / Director interest tracker
   ============================================================ */

const APPT_STATUSES = [
    { value: "pending", label: "Pending", cls: "bg-slate-100 text-slate-700" },
    { value: "appointed", label: "Appointed", cls: "bg-emerald-100 text-emerald-800" },
    { value: "declined", label: "Declined", cls: "bg-red-100 text-red-700" },
];
const APPT_POSITIONS = [
    { value: "regional_governor", label: "Regional Governor" },
    { value: "director", label: "Director" },
];

function emptyAppointmentForm() {
    return { user_id: null, position: "regional_governor", region_id: "", title: "", notes: "", status: "pending" };
}

function AppointmentDialog({ appt, members, regions, onSaved, trigger }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyAppointmentForm());
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!open) return;
        if (appt) {
            setForm({
                user_id: appt.user_id || null,
                position: appt.position || "regional_governor",
                region_id: appt.region_id || "",
                title: appt.title || "",
                notes: appt.notes || "",
                status: appt.status || "pending",
            });
        } else {
            setForm(emptyAppointmentForm());
        }
    }, [open, appt]);

    const memberOptions = useMemo(
        () => (members || []).map((m) => ({ id: m.id, name: m.name, email: m.email })),
        [members],
    );

    async function save() {
        if (!form.user_id) { toast.error("Pick a member first."); return; }
        if (form.position === "regional_governor" && !form.region_id) {
            toast.error("Pick a region for the governor appointment.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                user_id: form.user_id,
                position: form.position,
                region_id: form.position === "regional_governor" ? form.region_id : "",
                title: form.position === "director" ? form.title.trim() : "",
                notes: form.notes.trim(),
                status: form.status,
            };
            if (appt) {
                await api.put(`/admin/appointments/${appt.id}`, payload);
            } else {
                await api.post("/admin/appointments", payload);
            }
            toast.success("Saved");
            setOpen(false);
            onSaved && onSaved();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {trigger ? <span onClick={() => setOpen(true)}>{trigger}</span> : null}
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="appointment-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{appt ? "Edit appointment interest" : "Record appointment interest"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-3">
                    <div>
                        <Label>Member</Label>
                        <div className="mt-1.5">
                            <MemberPicker
                                options={memberOptions}
                                value={form.user_id}
                                onChange={(m) => setForm({ ...form, user_id: m?.id || null })}
                                placeholder="Search by name or email…"
                                testid="appointment-member"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>Position</Label>
                        <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                            <SelectTrigger className="rounded-xl mt-1.5" data-testid="appointment-position"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {APPT_POSITIONS.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    {form.position === "regional_governor" ? (
                        <div>
                            <Label>Region</Label>
                            <Select value={form.region_id || undefined} onValueChange={(v) => setForm({ ...form, region_id: v })}>
                                <SelectTrigger className="rounded-xl mt-1.5" data-testid="appointment-region"><SelectValue placeholder="Pick a region…" /></SelectTrigger>
                                <SelectContent>
                                    {(regions || []).map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
                                </SelectContent>
                            </Select>
                        </div>
                    ) : (
                        <div>
                            <Label>Director title (optional)</Label>
                            <Input
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                placeholder="e.g. Director of Communications"
                                className="rounded-xl mt-1.5"
                                data-testid="appointment-title"
                            />
                        </div>
                    )}
                    <div>
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger className="rounded-xl mt-1.5" data-testid="appointment-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {APPT_STATUSES.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Notes (optional)</Label>
                        <Textarea
                            rows={2}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Context, dates offered, reason declined, etc."
                            className="rounded-xl mt-1.5"
                            data-testid="appointment-notes"
                        />
                    </div>
                </div>
                <DialogFooter className="mt-4">
                    <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                    <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={save} disabled={busy} data-testid="appointment-save-btn">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function AppointmentsAdmin() {
    const [rows, setRows] = useState([]);
    const [members, setMembers] = useState([]);
    const [regions, setRegions] = useState([]);
    const [filter, setFilter] = useState("all"); // all | regional_governor | director

    const load = () => api.get("/admin/appointments").then(({ data }) => setRows(data || [])).catch(() => setRows([]));
    useEffect(() => {
        load();
        api.get("/members").then(({ data }) => setMembers(data || [])).catch(() => setMembers([]));
        // regions endpoint returns { regions: [...] } shape
        api.get("/regions").then(({ data }) => setRegions((data?.regions || []).map((r) => ({ id: r.id, name: r.name })))).catch(() => setRegions([]));
    }, []);

    async function remove(id) {
        if (!confirm("Delete this appointment record?")) return;
        try {
            await api.delete(`/admin/appointments/${id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed");
        }
    }

    const filtered = useMemo(() => {
        if (filter === "all") return rows;
        return rows.filter((r) => r.position === filter);
    }, [rows, filter]);

    function positionLabel(r) {
        if (r.position === "regional_governor") {
            return `Regional Governor · ${r.region_name || "—"}`;
        }
        return `Director${r.title ? ` · ${r.title}` : ""}`;
    }

    function statusBadge(status) {
        const s = APPT_STATUSES.find((x) => x.value === status) || APPT_STATUSES[0];
        return <Badge className={`${s.cls} rounded-full text-[10px] uppercase tracking-wider font-semibold`}>{s.label}</Badge>;
    }

    return (
        <div>
            <div className="flex justify-between items-center gap-3 flex-wrap mb-4">
                <div className="inline-flex gap-1 rounded-full bg-muted p-1">
                    <button type="button" className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === "all" ? "bg-white shadow text-primary" : "text-slate-600"}`} onClick={() => setFilter("all")} data-testid="appt-filter-all">All ({rows.length})</button>
                    <button type="button" className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === "regional_governor" ? "bg-white shadow text-primary" : "text-slate-600"}`} onClick={() => setFilter("regional_governor")} data-testid="appt-filter-governor">Governors ({rows.filter((r) => r.position === "regional_governor").length})</button>
                    <button type="button" className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === "director" ? "bg-white shadow text-primary" : "text-slate-600"}`} onClick={() => setFilter("director")} data-testid="appt-filter-director">Directors ({rows.filter((r) => r.position === "director").length})</button>
                </div>
                <AppointmentDialog
                    members={members}
                    regions={regions}
                    onSaved={load}
                    trigger={
                        <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-appointment-btn">
                            <Plus className="h-4 w-4 mr-1" /> New appointment
                        </Button>
                    }
                />
            </div>

            {filtered.length === 0 ? (
                <div className="text-muted-foreground text-center py-16 border border-dashed border-border rounded-2xl" data-testid="appointments-empty">
                    {rows.length === 0 ? "No appointments recorded yet. Add the first one to start tracking interest." : "No matches for that filter."}
                </div>
            ) : (
                <div className="overflow-x-auto bg-white border border-slate-200 rounded-2xl">
                    <table className="w-full text-sm" data-testid="appointments-table">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="px-4 py-3">Member</th>
                                <th className="px-4 py-3">Interest</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Notes</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} className="border-b border-border/60 hover:bg-muted/30" data-testid={`appointment-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold">{r.user_name || "—"}</div>
                                        {r.user_email && <div className="text-xs text-muted-foreground">{r.user_email}</div>}
                                    </td>
                                    <td className="px-4 py-3 font-medium">{positionLabel(r)}</td>
                                    <td className="px-4 py-3">{statusBadge(r.status)}</td>
                                    <td className="px-4 py-3 text-muted-foreground max-w-xs truncate" title={r.notes || ""}>{r.notes || "—"}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex gap-1">
                                            <AppointmentDialog
                                                appt={r}
                                                members={members}
                                                regions={regions}
                                                onSaved={load}
                                                trigger={
                                                    <Button variant="outline" size="sm" className="rounded-full" data-testid={`edit-appointment-${r.id}`}>
                                                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                                                    </Button>
                                                }
                                            />
                                            <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`delete-appointment-${r.id}`}>
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

